/**
 * Categories — the buckets an expense or an income lands in (plan step 1.1).
 *
 * Every household gets its own copy of the default list at signup (DBD-007), which is why
 * renaming, reordering and hiding are plain updates here: they change this family's list and
 * nobody else's. No DELETE grant exists — a hidden category keeps the entries already filed
 * under it.
 */
import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';
import { fetchPreferences } from '../../preferences/data/preferencesRepository.js';

export type CategoryType = 'expense' | 'income';

export type Category = Readonly<{
  id: string;
  /** Carried so the duplicate-name rule can say which household it is about. */
  householdId: string;
  name: string;
  categoryType: CategoryType;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
  isFavorite: boolean;
  icon: string | null;
}>;

export async function fetchCategories(): Promise<readonly Category[]> {
  const [categoriesResult, preferences] = await Promise.all([
    supabase
      .from('categories')
      .select(
        'id, household_id, name, category_type, parent_id, sort_order, is_active, is_system, icon',
      )
      .order('sort_order')
      .order('name'),
    fetchPreferences(),
  ]);
  if (categoriesResult.error) throw appErrorException(categoriesResult.error);

  const favorites = new Set(preferences.favoriteCategoryIds);

  return (categoriesResult.data ?? []).map((row) => ({
    id: row.id,
    householdId: row.household_id,
    name: row.name,
    categoryType: row.category_type as CategoryType,
    parentId: row.parent_id,
    sortOrder: row.sort_order,
    isActive: row.is_active,
    isSystem: row.is_system,
    isFavorite: favorites.has(row.id),
    icon: row.icon,
  }));
}

export async function createCategory(
  householdId: string,
  input: { name: string; categoryType: CategoryType; parentId?: string; icon?: string },
): Promise<string> {
  const { data, error } = await supabase
    .from('categories')
    .insert({
      household_id: householdId,
      name: input.name.trim(),
      category_type: input.categoryType,
      ...(input.parentId === undefined || input.parentId === ''
        ? {}
        : { parent_id: input.parentId }),
      // One of the icon keys in shared/ui/icons/CategoryIcon.tsx. Categories seeded before
      // this screen existed have none, and fall back to a name-based icon.
      ...(input.icon === undefined || input.icon === '' ? {} : { icon: input.icon }),
      // New categories sort after the seeded ones, which start at 10 and step by 10.
      sort_order: 1000,
    })
    .select('id')
    .single();
  if (error) throw appErrorException(error);
  return data.id;
}

export async function updateCategory(
  categoryId: string,
  patch: { name?: string; icon?: string; isActive?: boolean },
): Promise<void> {
  const { error } = await supabase
    .from('categories')
    .update({
      ...(patch.name === undefined ? {} : { name: patch.name.trim() }),
      ...(patch.icon === undefined ? {} : { icon: patch.icon }),
      ...(patch.isActive === undefined ? {} : { is_active: patch.isActive }),
    })
    .eq('id', categoryId);
  if (error) throw appErrorException(error);
}

/**
 * Writes a new order for one group of siblings.
 *
 * The desired order arrives as a list; each row gets `(index + 1) * 10`, and only the rows
 * whose value actually changes are written. Renumbering the whole group rather than swapping
 * two values is what makes the seeded list (10, 20, 30…) and hand-added categories (all
 * created with the same sort_order) behave the same way.
 */
export async function applyCategoryOrder(
  ordered: ReadonlyArray<{ id: string; sortOrder: number }>,
): Promise<void> {
  const changed = ordered
    .map((category, index) => ({ id: category.id, sortOrder: (index + 1) * 10 }))
    .filter((next, index) => next.sortOrder !== ordered[index]?.sortOrder);

  for (const category of changed) {
    const { error } = await supabase
      .from('categories')
      .update({ sort_order: category.sortOrder })
      .eq('id', category.id);
    if (error) throw appErrorException(error);
  }
}
